import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  FileText, Upload, Search, Filter, Download, Eye, Trash2, 
  CheckCircle, Clock, XCircle, File, Image, FileIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: '7_12', label: '7/12 Extract' },
  { value: 'agriculture_certificate', label: 'Agriculture Certificate' },
  { value: 'aadhaar_card', label: 'Aadhaar Card' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'passport_photo', label: 'Passport Photo' },
  { value: 'payment_receipt', label: 'Payment Receipt' },
  { value: 'other', label: 'Other Document' },
];

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'amber', icon: Clock },
  under_review: { label: 'Under Review', color: 'blue', icon: Clock },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'red', icon: XCircle },
};

const DocumentsPage = () => {
  const { user } = useOutletContext();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/crm/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedType) {
      toast.error('Please select a document type first');
      return;
    }

    const token = localStorage.getItem('crm_token');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/crm/documents/upload?document_type=${selectedType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Document uploaded successfully');
      fetchDocuments();
      setSelectedType('');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
    if (mimeType?.includes('pdf')) {
      return <FileText className="w-8 h-8 text-red-500" />;
    }
    return <File className="w-8 h-8 text-stone-500" />;
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return doc.documentName?.toLowerCase().includes(query) ||
             doc.submissionId?.toLowerCase().includes(query);
    }
    return true;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">Documents</h1>
          <p className="text-stone-500 mt-1">Upload and manage your documents</p>
        </div>
      </div>

      {/* Upload Section */}
      <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900 mb-1">Upload New Document</h3>
              <p className="text-sm text-emerald-700">Select document type and upload your file</p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full md:w-48 bg-white">
                  <SelectValue placeholder="Document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !selectedType}
                className="bg-emerald-900 hover:bg-emerald-800 whitespace-nowrap"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </span>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Checklist for Customers */}
      {user?.role === 'customer' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Required Documents Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DOCUMENT_TYPES.filter(t => t.value !== 'other').map((type) => {
                const uploaded = documents.find(d => d.documentType === type.value);
                const status = uploaded?.status;
                const StatusIcon = status ? STATUS_CONFIG[status]?.icon : Clock;
                
                return (
                  <div 
                    key={type.value}
                    className={`p-4 rounded-lg border ${
                      uploaded 
                        ? `bg-${STATUS_CONFIG[status]?.color || 'stone'}-50 border-${STATUS_CONFIG[status]?.color || 'stone'}-200`
                        : 'bg-stone-50 border-stone-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-stone-900">{type.label}</span>
                      {uploaded ? (
                        <span className={`flex items-center gap-1 text-xs text-${STATUS_CONFIG[status]?.color || 'stone'}-700`}>
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_CONFIG[status]?.label}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600">Not uploaded</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>My Documents ({filteredDocuments.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No documents uploaded yet</p>
              <p className="text-sm text-stone-400 mt-1">Upload your first document using the form above</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredDocuments.map((doc) => {
                const StatusIcon = STATUS_CONFIG[doc.status]?.icon || Clock;
                const statusColor = STATUS_CONFIG[doc.status]?.color || 'stone';
                
                return (
                  <div key={doc.id} className="p-4 flex items-center gap-4 hover:bg-stone-50">
                    <div className="flex-shrink-0">
                      {getFileIcon(doc.mimeType)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{doc.documentName}</p>
                      <p className="text-xs text-stone-500">
                        {DOCUMENT_TYPES.find(t => t.value === doc.documentType)?.label || doc.documentType}
                        {' • '}{formatFileSize(doc.fileSize)}
                        {' • '}{new Date(doc.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-stone-400 font-mono">{doc.submissionId}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-700`}>
                        <StatusIcon className="w-3 h-3" />
                        {STATUS_CONFIG[doc.status]?.label}
                      </span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-stone-100 rounded-lg"
                      >
                        <Eye className="w-4 h-4 text-stone-500" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentsPage;
